/* ============================================================================
   galleryBus.ts — micro event bus pour le mode galerie.

   Le canvas central (CreationCanvas) est l'autorite de l'index courant +
   du timer auto-rotate. La sidebar gauche (GallerySection) doit pouvoir :
     1. Envoyer des commandes au canvas : prev, next, set(i), pause, play
     2. Recevoir l'index courant pour l'afficher (compteur + thumb selected)

   Implementation : window.dispatchEvent + addEventListener. Zero dep, zero
   global state, lifecycle clean (useEffect + cleanup).
   ============================================================================ */

import { useEffect } from 'react';

/** Commandes emises par la sidebar vers le canvas. */
export type GalleryCommand = 'prev' | 'next' | 'set';

const COMMAND_EVENT = 'marthe-gallery-command';
const INDEX_CHANGE_EVENT = 'marthe-gallery-index';

interface CommandDetail {
  cmd: GalleryCommand;
  index?: number;
}

interface IndexDetail {
  index: number;
}

/**
 * Dispatch d'une commande gallery vers le canvas. Utilise par la sidebar +
 * la mini barre de navigation in-canvas.
 */
export function dispatchGalleryEvent(cmd: GalleryCommand, index?: number): void {
  if (typeof window === 'undefined') return;
  const detail: CommandDetail = { cmd, index };
  window.dispatchEvent(new CustomEvent<CommandDetail>(COMMAND_EVENT, { detail }));
}

/**
 * Hook pour le canvas : ecoute les commandes et appelle un handler.
 */
export function useGalleryCommands(handler: (detail: CommandDetail) => void): void {
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const listener = (e: Event) => {
      const ce = e as CustomEvent<CommandDetail>;
      handler(ce.detail);
    };
    window.addEventListener(COMMAND_EVENT, listener);
    return () => window.removeEventListener(COMMAND_EVENT, listener);
  }, [handler]);
}

/**
 * Notifie l'index courant — emis par le canvas a chaque changement (auto ou
 * manuel). Permet a la sidebar de mettre a jour le compteur + le thumb actif.
 */
export function publishGalleryIndex(index: number): void {
  if (typeof window === 'undefined') return;
  const detail: IndexDetail = { index };
  window.dispatchEvent(new CustomEvent<IndexDetail>(INDEX_CHANGE_EVENT, { detail }));
}

/**
 * Hook pour la sidebar : recoit les changements d'index et les passe a un
 * setter local.
 */
export function useGalleryIndex(setter: (index: number) => void): void {
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const listener = (e: Event) => {
      const ce = e as CustomEvent<IndexDetail>;
      setter(ce.detail.index);
    };
    window.addEventListener(INDEX_CHANGE_EVENT, listener);
    return () => window.removeEventListener(INDEX_CHANGE_EVENT, listener);
  }, [setter]);
}
